import { EvoIframeMfesProvider } from '@access-evo/mfe-react-host';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ProductProvider, useProduct } from '@workspace/utils-react';
import { AuthProvider } from 'react-oidc-context';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { CreditControlWithUserContext } from './components/CreditControlWithUserContext';
import type { PidgetSettings } from './helpers/PidgetSettings';
import LoginCallback from './LoginCallback';
import { AuthBridgeProvider } from './providers/AuthBridgeProvider';
import { CookieConsentProvider } from './providers/CookieConsentProvider';
import { UserContextProvider } from './providers/UserContextProvider';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const isEmptyInstanceId = (instanceId: string | null | undefined): boolean => {
  return !instanceId || instanceId === '' || instanceId === EMPTY_GUID;
};

// Clear stale OIDC storage on initial app load (runs once)
// This ensures we don't use cached tokens from a previous user after logout/re-login
const clearStaleOidcStorageOnLoad = (() => {
  let hasCleared = false;
  return (authority: string, clientId: string) => {
    if (!hasCleared) {
      const oidcKey = `oidc.user:${authority}:${clientId}`;
      sessionStorage.removeItem(oidcKey);
      hasCleared = true;
    }
  };
})();

// Best practice: clean up URL after successful sign-in (removes code, state params)
const onSigninCallback = (_user: unknown): void => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

// Capture deeplink query params from the initial URL before OIDC auth
// potentially strips them via onSigninCallback's history.replaceState.
const captureDeepLinkParams = (() => {
  let hasCaptured = false;
  return () => {
    if (!hasCaptured) {
      const params = new URLSearchParams(window.location.search);
      const customerId = params.get('customerId');
      if (customerId) {
        sessionStorage.setItem('deeplink_customerId', customerId);
      }
      const disputeId = params.get('disputeId');
      if (disputeId) {
        sessionStorage.setItem('deeplink_disputeId', disputeId);
      }
      const companyId = params.get('companyId');
      if (companyId) {
        sessionStorage.setItem('selectedCompanyId', companyId);
      }
      hasCaptured = true;
    }
  };
})();
captureDeepLinkParams();

export const AppContainer = (): React.ReactElement => {
  const queryClient = new QueryClient();
  const product = useProduct<PidgetSettings>();

  if (product?.settings?.identityUrl) {
    const effectiveInstanceId = isEmptyInstanceId(product.instanceId)
      ? product.settings.fallbackInstanceId
      : product.instanceId;

    // Clear stale OIDC storage once on app load to handle logout/re-login with different user
    clearStaleOidcStorageOnLoad(
      product.settings.identityUrl,
      product.settings.clientId,
    );

    return (
      <QueryClientProvider client={queryClient}>
        <ProductProvider iframePidget={product}>
          <AuthProvider
            client_id={product.settings.clientId}
            redirect_uri={window.location.origin}
            silent_redirect_uri={`${window.location.origin}/login/callback`}
            scope={product.settings.scope}
            authority={product.settings.identityUrl}
            onSigninCallback={onSigninCallback}
            automaticSilentRenew={true}
            accessTokenExpiringNotificationTimeInSeconds={60}
          >
            <AuthBridgeProvider instanceId={effectiveInstanceId}>
              <CookieConsentProvider
                policyUrl={product.settings.cookiePolicyUrl}
              >
                <EvoIframeMfesProvider
                  environment={product.settings.evoMfeOpenComponentEnvironment}
                >
                  <UserContextProvider apiBaseUrl={product.settings.apiUrl}>
                    <CreditControlWithUserContext
                      apiUrl={product.settings.apiUrl}
                      wootricToken={product.settings.wootricToken}
                      environment={
                        product.settings.evoMfeOpenComponentEnvironment
                      }
                      feedbackAccountToken={
                        product.settings.feedbackAccountToken
                      }
                      feedbackApiHost={product.settings.feedbackApiHost}
                      feedbackMfeVersion={product.settings.feedbackMfeVersion}
                      feedbackReDisplayAfterDays={
                        product.settings.feedbackReDisplayAfterDays
                      }
                      canUseDemoData={product.settings.canUseDemoData}
                      canForceSync={product.settings.canForceSync}
                      intercomAppId={product.settings.intercomAppId}
                      documentApiUrl={product.settings.documentApiUrl}
                    />
                  </UserContextProvider>
                </EvoIframeMfesProvider>
              </CookieConsentProvider>
            </AuthBridgeProvider>
          </AuthProvider>
        </ProductProvider>
      </QueryClientProvider>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login/callback" element={<LoginCallback />} />
      </Routes>
    </BrowserRouter>
  );
};
